/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getUrlSync, isChannelTypeB2B } from '@msdyn365-commerce/core';
import { SalesInvoiceLine, SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { IInvoiceErrorState, InvoiceErrorComponent } from '@msdyn365-commerce-modules/invoice';
import { getSimpleProductsAction, ProductInput } from '@msdyn365-commerce-modules/retail-actions';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { InvoiceDetailsHeaderComponent } from './components/invoice-details-header';
import { InvoiceDetailsPaymentDetailsComponent } from './components/invoice-details-payment-summary';
import { InvoiceDetailsProductsComponent } from './components/invoice-details-products';
import { InvoiceDetailsModel } from './helpers/invoice-data';
import { IInvoiceDetailsData } from './invoice-details.data';
import { IInvoiceDetailsProps } from './invoice-details.props.autogenerated';

export interface IInvoiceDetailsViewState {
    invoiceDetails?: InvoiceDetailsModel;
    payInvoiceErrorModalOpen?: boolean;
    errorState?: IInvoiceErrorState;
    isContactModelInvoice?: boolean;
}

export interface IInvoiceDetailsViewProps extends IInvoiceDetailsProps<IInvoiceDetailsData> {
    moduleProps: IModuleProps;
    header: JSX.Element;
    products: JSX.Element;
    paymentDetails: JSX.Element;
    payInvoiceError?: JSX.Element;
}

/**
 * InvoiceDetails component.
 * @extends {React.PureComponent<IInvoiceDetailsProps<IInvoiceDetailsData>>}
 */
class InvoiceDetails extends React.PureComponent<IInvoiceDetailsProps<IInvoiceDetailsData>, IInvoiceDetailsViewState> {
    private static readonly _className: string = 'msc-invoice-details';
    private readonly retailB2BContactMultiOutletOrderingFeatureName = 'Dynamics.AX.Application.RetailB2BContactMultiOutletOrderingFeature';

    public constructor(props: IInvoiceDetailsProps<IInvoiceDetailsData>) {
        super(props);

        this.state = { errorState: { errorMessage: '', otherError: '' } };
        this._onAddToCartFailed = this._onAddToCartFailed.bind(this);
        this._setPayInvoiceErrorModalClose = this._setPayInvoiceErrorModalClose.bind(this);
    }

    public async componentDidMount(): Promise<void> {
        const invoice = await this.props.data.invoice;
        const invoiceDetails = await this.props.data.invoiceDetails;

        const products = await this._getProducts(invoiceDetails.SalesInvoiceLine, invoiceDetails.ChannelId);
        const featureState = (await this.props.data.featureStates) ?? [];
        const currentBusinessPartnerUser = await this.props.data.b2bUserInformation;
        const isB2BSite = isChannelTypeB2B(this.props.context.request);
        const isRetailB2BContactMultiOutletOrderingFeatureEnabled =
            featureState.find(feature => feature.Name === this.retailB2BContactMultiOutletOrderingFeatureName)?.IsEnabled ?? false;
        const isContactModelInvoice = isB2BSite && isRetailB2BContactMultiOutletOrderingFeatureEnabled;

        const invoiceDetailsModel = new InvoiceDetailsModel(
            invoice,
            invoiceDetails,
            products,
            currentBusinessPartnerUser,
            isContactModelInvoice,
            this.props.resources
        );

        this.setState({
            invoiceDetails: invoiceDetailsModel,
            payInvoiceErrorModalOpen: false,
            errorState: { errorMessage: '', otherError: '' },
            isContactModelInvoice
        });
    }

    public render(): JSX.Element | null {
        if (!this.state.invoiceDetails) {
            return null;
        }

        const header = this._renderHeader();
        const products = this._renderProducts();
        const paymentDetails = this._renderPaymentDetails();
        const payInvoiceError = this._renderInvoiceErrors();

        const props: IInvoiceDetailsViewProps = {
            ...(this.props as IInvoiceDetailsProps<IInvoiceDetailsData>),
            moduleProps: {
                moduleProps: this.props,
                className: InvoiceDetails._className
            },
            header,
            products,
            paymentDetails,
            payInvoiceError
        };

        return this.props.renderView(props);
    }

    private async _getProducts(invoiceLines: SalesInvoiceLine[] = [], channelId?: number): Promise<SimpleProduct[]> {
        const productInputs = invoiceLines
            .filter(product => product.ProductId)
            .map(
                product =>
                    new ProductInput(
                        product.ProductId!,
                        this.props.context.request.apiSettings,
                        channelId,
                        undefined,
                        this.props.context.actionContext.requestContext
                    )
            );
        return getSimpleProductsAction(productInputs, this.props.context.actionContext);
    }

    private _renderHeader(): JSX.Element {
        const invoiceDetails = this.state.invoiceDetails!;
        return (
            <InvoiceDetailsHeaderComponent
                className={`${InvoiceDetails._className}__header`}
                id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                data={{ invoiceDetails }}
                typeName={this.props.typeName}
                context={this.props.context}
                resources={this.props.resources}
                onError={this._onAddToCartFailed}
                isContactModelInvoice={this.state.isContactModelInvoice}
            />
        );
    }

    private _renderProducts(): JSX.Element {
        const invoiceDetails = this.state.invoiceDetails!;
        const { imageSettings } = this.props.config;
        return (
            <InvoiceDetailsProductsComponent
                className={`${InvoiceDetails._className}__products`}
                id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                data={{ invoiceDetails }}
                typeName={this.props.typeName}
                context={this.props.context}
                resources={this.props.resources}
                imageSettings={imageSettings}
            />
        );
    }

    private _renderPaymentDetails(): JSX.Element {
        const invoiceDetails = this.state.invoiceDetails!;
        return (
            <InvoiceDetailsPaymentDetailsComponent
                className={`${InvoiceDetails._className}__payment-details`}
                id={`${InvoiceDetails._className}__${invoiceDetails.id}`}
                data={{ invoicePaymentDetails: invoiceDetails }}
                typeName={this.props.typeName}
                context={this.props.context}
                resources={this.props.resources}
            />
        );
    }

    private _onAddToCartFailed(newErrorState: IInvoiceErrorState): void {
        const errorState = { ...newErrorState };
        errorState.errorHost = 'ADDINVOICETOCART';

        // check for errorcode and otherError which contains the LocalizedMessage from API
        if (
            errorState &&
            errorState.errorCode === 'Microsoft_Dynamics_Commerce_Runtime_InvoiceLineAlreadyExistsInCart' &&
            errorState.otherError
        ) {
            this.setState({ errorState: errorState });
        }

        this.setState({ payInvoiceErrorModalOpen: true });
    }

    private _setPayInvoiceErrorModalClose(): void {
        this.setState({ payInvoiceErrorModalOpen: false });
    }

    private _renderInvoiceErrors(): JSX.Element {
        const {
            resources: {
                payInvoiceErrorDialogHeader,
                payInvoiceErrorDialogBodyErrorTitle,
                payInvoiceErrorDialogBodyErrorDescription,
                payInvoiceErrorDialogFooterGoToCartButtonText,
                payInvoiceErrorDialogFooterCancelButtonText
            }
        } = this.props;

        const cartUrl = getUrlSync('cart', this.props.context.actionContext) || '';

        const errorClassName = `${InvoiceDetails._className}__errors`;

        return (
            <InvoiceErrorComponent
                id={errorClassName}
                typeName={this.props.typeName}
                data={{}}
                context={this.props.context}
                payInvoiceErrorModalOpen={this.state.payInvoiceErrorModalOpen || false}
                showError={false}
                errorDialogHeader={payInvoiceErrorDialogHeader}
                errorDialogBodyErrorTitle={payInvoiceErrorDialogBodyErrorTitle}
                // show otherError (localizedMessage) from API failure to dialog box
                errorDialogBodyErrorDescription={this.state.errorState?.otherError || payInvoiceErrorDialogBodyErrorDescription}
                errorDialogFooterGoToCartButtonText={payInvoiceErrorDialogFooterGoToCartButtonText}
                errorDialogFooterCancelButtonText={payInvoiceErrorDialogFooterCancelButtonText}
                cartUrl={cartUrl}
                setPayInvoiceErrorModalClose={this._setPayInvoiceErrorModalClose}
            />
        );
    }
}

export default InvoiceDetails;
